import React, { useState, useEffect, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { useNotification } from "@hooks/useNotification";
import { useReduxSelector } from "@hooks/useReduxSelector";
import { useUserPermission } from "@hooks/users";
import ReactExport from "react-data-export";

import LockCollapse from "@components/disclosure/LockCollapse";
import Button from "@components/tailwindComponents/Button";
import ListboxCollapse from "@components/tailwindComponents/ListboxCollapse";
import CustomSwitch from "@components/tailwindComponents/Switch";

import {
  AllToHeader as AllConstToHeader,
  AllToHeaderExpectBanking as AllConstToHeaderExpectBanking,
  dataForListBoxInfoPers,
  dataForListBoxEmploi as staticDataForListBoxEmploi,
  dataForListBoxCertifications,
  dataForListBoxMaterial,
  dataListForBankingInformation,
  HeaderAddress,
  HeaderBankingInformation,
  HeaderCertification,
  HeaderClothes,
  HeaderCourriels,
  HeaderDisciplinary,
  HeaderEmergencyContacts,
  HeaderEmploi,
  HeaderFormation,
  HeaderHealth,
  HeaderIndisponibilites,
  HeaderInfoPers,
  HeaderMaterial,
  HeaderNote,
  HeaderParcours,
  HeaderVacances as StaticHeaderVancances
} from "./_config/exportation.config";
import { getExcelValue } from "./_config/exportation.utils";
import { getOtherHolidays } from "@helpers/_functions";

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

export default function ExportDataSelection({ employeSelected }) {
  const { t, i18n } = useTranslation("pages.employés.exportation");
  const { showWarning } = useNotification();
  const { isActiveBankingInformationAndInsuranceAccess } = useUserPermission();

  const useAdditionalInfo =
    useReduxSelector(
      ({ settings }) => settings?.enterprise?.useAdditionalInfo
    ) || false;
  const addionalInfoList = useReduxSelector(
    ({ settings }) => settings.additionalInformation?.list || []
  );

  const [headersExport, setHeadersExport] = useState([]);
  const [selectedInfoPers, setSelectedInfoPers] = useState([]);
  const [selectedEmploi, setSelectedEmploi] = useState([]);
  const [selectedCertifications, setSelectedCertifications] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState([]);
  const [selectedInformationBanking, setSelectedInformationBanking] = useState(
    []
  );

  // 🧩 Gestion comptes de vacances 🧩
  const employeeId = useReduxSelector(({ userConnected }) => userConnected._id);

  const holidays = useReduxSelector(
    ({ settings }) => settings.holidayName.list || []
  );

  const otherHolidays = useMemo(() => {
    return getOtherHolidays({
      holidayList: holidays,
      language: i18n.language,
      employeeId: employeeId
    });
  }, [holidays, i18n.language, employeeId]);

  const HeaderVacances = useMemo(() => {
    return [
      ...StaticHeaderVancances,
      ...otherHolidays.flatMap((holiday, index) => {
        return [
          {
            id: 163 + (index * 2 + 1) / 100,
            label: `${holiday.designation} permises par année`,
            value: `holidaySolde.${holiday._id}.hours`
          },
          {
            id: 163 + (index * 2 + 2) / 100,
            label: `${holiday.designation} restantes`,
            value: `holidaySolde.${holiday._id}.hoursRemaining`
          }
        ];
      })
    ];
  }, [otherHolidays]);

  const dataForListBoxEmploi = useMemo(() => {
    return [
      ...staticDataForListBoxEmploi,
      {
        id: 3,
        label: t("Vacances"),
        data: [...HeaderVacances]
      }
    ];
  }, [HeaderVacances]);

  // 🧩 Gestion infos complémentaires 🧩
  const HeaderAdditionalInfos = useMemo(() => {
    return useAdditionalInfo
      ? addionalInfoList?.map((item, index) => ({
          id: 31 + index,
          labelFr: item.designationFr,
          labelEn: item.designationEn,
          label:
            i18n.language === "fr" ? item.designationFr : item.designationEn,
          value: `personnalInformation.additionalInfo.${item._id}`
        }))
      : [];
  }, [addionalInfoList, useAdditionalInfo, i18n.language]);

  const customDataForListBoxInfoPers = useMemo(() => {
    const additionalInfo = useAdditionalInfo
      ? [
          {
            id: 6,
            label: t("Informations complémentaires"),
            data: HeaderAdditionalInfos
          }
        ]
      : [];
    return [...dataForListBoxInfoPers, ...additionalInfo];
  }, [useAdditionalInfo, t, HeaderAdditionalInfos]);

  const AllToHeader = useMemo(() => {
    return [...AllConstToHeader, ...HeaderAdditionalInfos, ...HeaderVacances];
  }, [HeaderAdditionalInfos, HeaderVacances]);

  const AllToHeaderExpectBanking = useMemo(() => {
    return [
      ...AllConstToHeaderExpectBanking,
      ...HeaderAdditionalInfos,
      ...HeaderVacances
    ];
  }, [HeaderAdditionalInfos, HeaderVacances]);

  const sortedHeadersExport = useMemo(() => {
    return headersExport.sort((a, b) => a.id - b.id);
  }, [headersExport]);

  useEffect(() => {
    setHeadersExport([
      ...selectedInfoPers,
      ...selectedEmploi,
      ...selectedCertifications,
      ...selectedMaterial,
      ...selectedInformationBanking
    ]);
  }, [
    selectedInfoPers,
    selectedEmploi,
    selectedCertifications,
    selectedMaterial,
    selectedInformationBanking
  ]);

  const selectAllHeaderExport = (ev) => {
    if (ev) {
      setSelectedInfoPers([
        ...HeaderInfoPers,
        ...HeaderCourriels,
        ...HeaderAddress,
        ...HeaderHealth,
        ...HeaderClothes,
        ...HeaderEmergencyContacts,
        ...HeaderAdditionalInfos
      ]);
      setSelectedEmploi([
        ...HeaderEmploi,
        ...HeaderIndisponibilites,
        ...HeaderParcours,
        ...HeaderVacances,
        ...HeaderDisciplinary
      ]);
      setSelectedCertifications([
        ...HeaderNote,
        ...HeaderCertification,
        ...HeaderFormation
      ]);
      setSelectedMaterial(HeaderMaterial);
      if (isActiveBankingInformationAndInsuranceAccess)
        setSelectedInformationBanking(HeaderBankingInformation);
      setHeadersExport(
        isActiveBankingInformationAndInsuranceAccess
          ? AllToHeader
          : AllToHeaderExpectBanking
      );
    } else {
      setSelectedInfoPers([]);
      setSelectedEmploi([]);
      setSelectedCertifications([]);
      setSelectedMaterial([]);
      setHeadersExport([]);
      setSelectedInformationBanking([]);
    }
  };

  const selectAllInfoPers = () => {
    setSelectedInfoPers([
      ...HeaderInfoPers,
      ...HeaderCourriels,
      ...HeaderAddress,
      ...HeaderHealth,
      ...HeaderClothes,
      ...HeaderEmergencyContacts
    ]);
  };

  const selectAllEmploi = () => {
    setSelectedEmploi([
      ...HeaderEmploi,
      ...HeaderIndisponibilites,
      ...HeaderParcours,
      ...HeaderVacances,
      ...HeaderDisciplinary,
      ...HeaderFormation
    ]);
  };

  const selectAllCertifications = () => {
    setSelectedCertifications([
      ...HeaderNote,
      ...HeaderCertification,
      ...HeaderFormation
    ]);
  };

  const selectAllMaterial = () => {
    setSelectedMaterial(HeaderMaterial);
  };

  const selectAllInformationBanking = () => {
    if (!isActiveBankingInformationAndInsuranceAccess)
      return showWarning(
        "Accès limité Veuillez demander l'accès à votre administrateur"
      );
    setSelectedInformationBanking(HeaderBankingInformation);
  };

  return (
    <div className="arh-flex arh-w-2/5 arh-flex-col arh-items-center arh-bg-greyColor arh-px-5 arh-py-8">
      <div className="arh-w-full arh-max-w-[25rem]">
        <div className="arh-mb-[3.25rem] arh-flex arh-w-full arh-items-center arh-justify-between">
          <h6 className="arh-font-poppins arh-text-sm arh-font-semibold arh-text-darkColor">
            {t("Données à exporter")}
          </h6>
          <div className="arh-flex arh-items-center arh-space-x-2 arh-font-poppins arh-text-[11px] arh-font-normal arh-text-darkColor">
            <span>{t("Tout sélectionner")}</span>
            <CustomSwitch
              color="arh-bg-greenColor"
              handleChange={selectAllHeaderExport}
              checked={
                headersExport?.length ===
                (isActiveBankingInformationAndInsuranceAccess
                  ? AllToHeader?.length
                  : AllToHeaderExpectBanking?.length)
              }
            />
          </div>
        </div>
        <div className="arh-flex arh-w-full arh-flex-col arh-space-y-5">
          <ListboxCollapse
            label={t("Informations personnelles")}
            placeholder={t("Informations personnelles")}
            dataList={customDataForListBoxInfoPers}
            dataSelected={selectedInfoPers}
            handleSelect={setSelectedInfoPers}
            selectAll={selectAllInfoPers}
          />
          <ListboxCollapse
            label={t("Emploi, salaire et vacances")}
            placeholder={t("Emploi, salaire et vacances")}
            dataList={dataForListBoxEmploi}
            dataSelected={selectedEmploi}
            handleSelect={setSelectedEmploi}
            selectAll={selectAllEmploi}
          />
          <ListboxCollapse
            label={t("Notes, certifications et formations")}
            placeholder={t("Notes, certifications et formations")}
            dataList={dataForListBoxCertifications}
            dataSelected={selectedCertifications}
            handleSelect={setSelectedCertifications}
            selectAll={selectAllCertifications}
          />
          <ListboxCollapse
            label={t("Matériel assigné")}
            placeholder={t("Matériel assigné")}
            dataList={dataForListBoxMaterial}
            dataSelected={selectedMaterial}
            handleSelect={setSelectedMaterial}
            selectAll={selectAllMaterial}
          />
          {isActiveBankingInformationAndInsuranceAccess ? (
            <ListboxCollapse
              label={t("Informations bancaires et d'assurance")}
              placeholder={t("Informations bancaires et d'assurance")}
              dataList={dataListForBankingInformation}
              dataSelected={selectedInformationBanking}
              handleSelect={setSelectedInformationBanking}
              selectAll={selectAllInformationBanking}
            />
          ) : (
            <LockCollapse
              label={t("Informations bancaires et d'assurance")}
              placeholder={t("Informations bancaires et d'assurance")}
              t={t}
            />
          )}
        </div>
        <div className="arh-mt-[74px] arh-w-full">
          <ExcelFile
            filename="Employe_export"
            element={
              <Button
                disabled={
                  !sortedHeadersExport?.length || !employeSelected?.length
                }
              >
                {`${t("Exporter Excel")} (.xls)`}
              </Button>
            }
          >
            <ExcelSheet
              data={employeSelected}
              name="Employees"
            >
              {sortedHeadersExport.map((fieldInfo) => {
                return (
                  <ExcelColumn
                    key={fieldInfo.id}
                    label={
                      fieldInfo.labelFr
                        ? i18n.language === "fr"
                          ? fieldInfo.labelFr
                          : fieldInfo.labelEn
                        : t(fieldInfo.label)
                    }
                    value={(employeeData) =>
                      getExcelValue(employeeData, fieldInfo)
                    }
                  />
                );
              })}
            </ExcelSheet>
          </ExcelFile>
        </div>
      </div>
    </div>
  );
}
